import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import { Dropdown, Button } from 'react-bootstrap';
import useAdvanceTable from 'hooks/common/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { Connector } from 'models/Connector';
import PhoenixDocCard from 'components/base/PhoenixDocCard';
import useCarrierConnectionConfig from 'hooks/useCarrierConnectionConfig';
import { getDefaultInvoiceDateRange } from 'helpers/utils';

// Simple download function
const downloadFile = async (signedUrl: string, fileName?: string) => {
  try {
    const response = await fetch(signedUrl);
    if (!response.ok) {
      throw new Error('Failed to fetch file');
    }
    
    // Try to get filename from Content-Disposition header
    let downloadFileName = fileName;
    const contentDisposition = response.headers.get('Content-Disposition');
    if (contentDisposition) {
      const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (fileNameMatch && fileNameMatch[1]) {
        downloadFileName = fileNameMatch[1].replace(/['"]/g, '');
      }
    }
    
    // If no filename from header, use provided fileName or extract from URL
    if (!downloadFileName) {
      const urlPath = new URL(signedUrl).pathname;
      const urlFileName = urlPath.split('/').pop();
      downloadFileName = urlFileName || 'invoice';
    }
    
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = downloadFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading file:', error);
    alert('Failed to download file. Please try again.');
  }
};

// Invoice type based on backend InvoiceDTO
type Invoice = {
  id: number;
  invoice_number: string;
  invoice_date: string;
  connector_id: number;
  connector_group_id: number;
  carrier?: string;
  file_format: string;
  file_format_code: string;
  output_format: string;
  s3_path: string;
  file: string;
  extracted_invoices: any;
  // Status will be calculated based on s3_path availability
};

// Table columns definition based on backend InvoiceDTO
const createInvoiceTableColumns = (
  resolveDisplayName: (carrierName?: string | null) => string,
): ColumnDef<Invoice>[] => [
  {
    accessorKey: 'invoice_number',
    header: 'Invoice Number',
    enableColumnFilter: true,
    filterFn: 'equals',
    meta: {
      cellProps: { className: 'ps-3 fs-9 text-900 white-space-nowrap py-4 text-start' },
      headerProps: { className: 'ps-3 text-start' }
    }
  },
  {
    accessorKey: 'connector_group_id',
    header: 'Group ID',
    enableColumnFilter: true,
    filterFn: 'equals',
    meta: {
      cellProps: { className: 'ps-3 fs-9 text-900 white-space-nowrap py-4 text-start' },
      headerProps: { className: 'ps-3 text-start' }
    }
  },
  {
    accessorKey: 'connector_id',
    header: 'Connector ID',
    enableColumnFilter: true,
    filterFn: 'equals',
    meta: {
      cellProps: { className: 'ps-3 fs-9 text-900 white-space-nowrap py-4 text-start' },
      headerProps: { className: 'ps-3 text-start' }
    }
  },
  {
    accessorKey: 'carrier',
    header: 'Carrier',
    enableColumnFilter: true,
    filterFn: 'equals',
    cell: ({ getValue }) => {
      const carrier = getValue<string | undefined>();
      if (!carrier) {
        return <span className="text-400 fst-italic">—</span>;
      }
      return resolveDisplayName(carrier);
    },
    meta: {
      cellProps: { className: 'ps-3 fs-9 text-900 white-space-nowrap py-4 text-start' },
      headerProps: { className: 'ps-3 text-start' }
    }
  },
  {
    accessorKey: 'file_format',
    header: 'File Format',
    enableColumnFilter: true,
    filterFn: (row, columnId, value) => {
      if (!value || (Array.isArray(value) && value.length === 0)) {
        return true;
      }
      const rowValue = row.getValue<string | undefined>(columnId);
      if (!rowValue) {
        return false;
      }
      return Array.isArray(value) && value.includes(rowValue);
    },
    meta: {
      cellProps: { className: 'ps-3 fs-9 text-900 white-space-nowrap py-4 text-start' },
      headerProps: { className: 'ps-3 text-start' }
    }
  },
  {
    accessorKey: 'invoice_date',
    header: 'Invoice Date',
    enableColumnFilter: true,
    filterFn: (row, columnId, value) => {
      if (!value || (typeof value === 'string' && value === '')) {
        return true;
      }

      const rowValue = row.getValue<string | undefined>(columnId);
      if (!rowValue) {
        return false;
      }

      const rowDate = new Date(rowValue);
      if (Number.isNaN(rowDate.getTime())) {
        return false;
      }

      const { start, end } = value as { start?: string; end?: string };

      if (start) {
        const startDate = new Date(start);
        if (!Number.isNaN(startDate.getTime()) && rowDate < startDate) {
          return false;
        }
      }

      if (end) {
        const endDate = new Date(end);
        if (!Number.isNaN(endDate.getTime())) {
          const endOfDay = new Date(endDate);
          endOfDay.setHours(23, 59, 59, 999);
          if (rowDate > endOfDay) {
            return false;
          }
        }
      }

      return true;
    },
    meta: {
      cellProps: { className: 'ps-3 fs-9 text-900 white-space-nowrap py-4 text-start' },
      headerProps: { className: 'ps-3 text-start' }
    }
  },
  {
    accessorKey: 'file',
    header: 'Download',
    enableColumnFilter: true,
    filterFn: 'includesString',
    cell: ({ getValue }) => {
      const fileUrl = getValue<string | undefined>();

      if (!fileUrl) {
        return <span className="text-400 fst-italic">—</span>;
      }

      const handleDownload = async (e: React.MouseEvent) => {
        e.preventDefault();
        await downloadFile(fileUrl);
      };

      return (
        <Button
          variant="link"
          size="sm"
          className="text-decoration-none fw-semibold p-0"
          onClick={handleDownload}
          style={{ textDecoration: 'none' }}
        >
          download
        </Button>
      );
    },
    meta: {
      cellProps: { className: 'ps-3 fs-9 text-900 white-space-nowrap py-4 text-start' },
      headerProps: { className: 'ps-3 text-start' }
    }
  }
];

// Props interface
interface InvoicesManagementPanelProps {
  resellerId: number;
  invoices: Invoice[];
  connectors?: Connector[];
  loading?: boolean;
  error?: string;
  onRefresh?: () => void;
  onLoadMore?: () => void | Promise<void>;
  hasNextPage?: boolean;
  loadingMore?: boolean;
  onDateRangeChange?: (startDate: string, endDate: string) => void;
  dateRange?: { start: string; end: string };
}

const InvoicesManagementPanel: React.FC<InvoicesManagementPanelProps> = ({
  resellerId,
  invoices,
  connectors = [],
  loading = false,
  error,
  onRefresh,
  onLoadMore,
  hasNextPage = false,
  loadingMore = false,
  onDateRangeChange,
  dateRange: externalDateRange
}) => {
  const { resolveDisplayName } = useCarrierConnectionConfig();
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>(
    externalDateRange || { start: '', end: '' }
  );

  // Sync with external dateRange prop
  useEffect(() => {
    if (externalDateRange) {
      setDateRange(externalDateRange);
    }
  }, [externalDateRange]);

  const [selectedFileFormat, setSelectedFileFormat] = useState<string[]>([]);

  const connectorsById = useMemo(() => {
    const map: Record<number, string> = {};
    connectors.forEach(connector => {
      if (connector?.id && connector.carrier) {
        map[connector.id] = connector.carrier;
      }
    });
    return map;
  }, [connectors]);

  const fileFormatCarrierMap = useMemo(() => {
    const map = new Map<string, string>();
    invoices.forEach(invoice => {
      const format = invoice.file_format?.trim();
      if (!format) return;
      const carrier = connectorsById[invoice.connector_id];
      if (carrier && !map.has(format)) {
        map.set(format, carrier);
      }
    });
    return map;
  }, [invoices, connectorsById]);

  const getFileFormatLabel = useCallback(
    (format: string) => {
      const carrier = fileFormatCarrierMap.get(format);
      return carrier ? `${format} (${resolveDisplayName(carrier)})` : format;
    },
    [fileFormatCarrierMap, resolveDisplayName]
  );

  const invoiceTableColumns = useMemo(
    () => createInvoiceTableColumns(resolveDisplayName),
    [resolveDisplayName],
  );

  // Extract unique file formats from invoices data
  const fileFormats = useMemo(() => {
    const formats = new Set<string>();
    invoices.forEach(invoice => {
      if (invoice.file_format && invoice.file_format.trim() !== '') {
        formats.add(invoice.file_format);
      }
    });
    return Array.from(formats).sort();
  }, [invoices]);

  // Date range is applied by the API. Re-filtering with `new Date('YYYY-MM-DD')`
  // drops start-date invoices in US timezones (parsed as UTC midnight).
  const table = useAdvanceTable({
    data: invoices,
    columns: invoiceTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    autoResetPageIndex: false
  });

  const { getColumn } = table;
  const pageIndex = table.getState().pagination.pageIndex;
  const pageCount = table.getPageCount();
  const loadMoreLock = useRef(false);
  const pendingAdvance = useRef(false);

  const requestMore = useCallback(() => {
    if (!onLoadMore || loadMoreLock.current || loadingMore || !hasNextPage) {
      return;
    }
    loadMoreLock.current = true;
    return Promise.resolve(onLoadMore()).finally(() => {
      loadMoreLock.current = false;
    });
  }, [onLoadMore, loadingMore, hasNextPage]);

  useEffect(() => {
    const onLastPage = pageCount > 0 && pageIndex >= pageCount - 1;
    if (!onLastPage || !hasNextPage) {
      return;
    }
    requestMore();
  }, [pageIndex, pageCount, hasNextPage, requestMore]);

  const handleNeedMore = useCallback(() => {
    pendingAdvance.current = true;
    requestMore();
  }, [requestMore]);

  useEffect(() => {
    if (!pendingAdvance.current || !table.getCanNextPage()) {
      return;
    }
    pendingAdvance.current = false;
    table.nextPage();
    // Intentionally omit `table`: the instance is a new object each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices.length, pageCount]);

  useEffect(() => {
    table.setPageIndex(0);
    // Reset only when the search window or local filters change, not when
    // more invoice rows are appended.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.start, dateRange.end, selectedFileFormat]);

  const clearAllFilters = useCallback(() => {
    ['file_format'].forEach(
      columnId => {
        getColumn(columnId)?.setFilterValue([]);
      }
    );
    const defaultRange = getDefaultInvoiceDateRange();
    setDateRange(defaultRange);
    setSelectedFileFormat([]);
    if (onDateRangeChange) {
      onDateRangeChange(defaultRange.start, defaultRange.end);
    }
  }, [getColumn, onDateRangeChange]);

  const getDataCount = useCallback(
    (columnId: string, value: string) => {
      if (columnId === 'file_format') {
        return invoices.filter(invoice => invoice.file_format === value).length;
      }
      return 0;
    },
    [invoices]
  );

  const handleFileFormatToggle = useCallback((format: string) => {
    setSelectedFileFormat(prev => {
      const newSelection = prev.includes(format)
        ? prev.filter(f => f !== format)
        : [...prev, format];

      getColumn('file_format')?.setFilterValue(newSelection.length > 0 ? newSelection : []);
      return newSelection;
    });
  }, [getColumn]);

  const handleSelectAllFormats = useCallback(() => {
    if (selectedFileFormat.length === fileFormats.length) {
      // Deselect all
      setSelectedFileFormat([]);
      getColumn('file_format')?.setFilterValue([]);
    } else {
      // Select all
      setSelectedFileFormat(fileFormats);
      getColumn('file_format')?.setFilterValue(fileFormats);
    }
  }, [selectedFileFormat.length, fileFormats, getColumn]);

  const fileFormatLabel = selectedFileFormat.length === 0
    ? 'All File Formats'
    : selectedFileFormat.length === 1
      ? getFileFormatLabel(selectedFileFormat[0])
      : `${selectedFileFormat.length} File Formats Selected`;

  const handleDateChange = useCallback(
    (key: 'start' | 'end') => (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setDateRange(prev => {
        const updated = { ...prev, [key]: newValue };
        if (updated.start && updated.end && onDateRangeChange) {
          onDateRangeChange(updated.start, updated.end);
        }
        return updated;
      });
    },
    [onDateRangeChange]
  );

  return (
    <PhoenixDocCard className="mb-4">
      <PhoenixDocCard.Body>
        <AdvanceTableProvider {...table}>
          <div className="mb-3">
            <div className="d-flex flex-wrap align-items-end gap-3">
              {/* Invoice Date Range Filter */}
              <div className="d-flex align-items-end gap-2">
                <div>
                  <label className="form-label mb-1 text-900 fw-semibold">Start Date</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={dateRange.start}
                    onChange={handleDateChange('start')}
                    style={{ minWidth: '150px' }}
                  />
                </div>
                <div>
                  <label className="form-label mb-1 text-900 fw-semibold">End Date</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={dateRange.end}
                    onChange={handleDateChange('end')}
                    style={{ minWidth: '150px' }}
                  />
                </div>
              </div>

              {/* File Format Filter */}
              <div>
                <label className="form-label mb-1 text-900 fw-semibold">File Format</label>
                <Dropdown className="position-static">
                  <Dropdown.Toggle
                    variant="phoenix-secondary"
                    className="px-7 dropdown-caret-none"
                    style={{ minWidth: '200px' }}
                  >
                    {fileFormatLabel}
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="py-2" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    <Dropdown.Item
                      onClick={handleSelectAllFormats}
                      className="d-flex align-items-center"
                    >
                      <input
                        type="checkbox"
                        className="form-check-input me-2"
                        checked={selectedFileFormat.length === fileFormats.length && fileFormats.length > 0}
                        onChange={() => { }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className="fw-semibold">Select All</span>
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    {fileFormats.map(format => {
                      const count = getDataCount('file_format', format);
                      const isSelected = selectedFileFormat.includes(format);
                      return (
                        <Dropdown.Item
                          key={format}
                          onClick={() => handleFileFormatToggle(format)}
                          className="d-flex justify-content-between align-items-center"
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="d-flex align-items-center">
                            <input
                              type="checkbox"
                              className="form-check-input me-2"
                              checked={isSelected}
                              onChange={() => { }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span>{getFileFormatLabel(format)}</span>
                          </div>
                          <span className="text-700 fw-semi-bold ms-3">({count})</span>
                        </Dropdown.Item>
                      );
                    })}
                  </Dropdown.Menu>
                </Dropdown>
              </div>

              {/* Clear All Filters Button */}
              <div>
                <label className="form-label mb-1 d-block">&nbsp;</label>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={clearAllFilters}
                  disabled={!dateRange.start && !dateRange.end && selectedFileFormat.length === 0}
                >
                  Clear All
                </button>
              </div>
            </div>
          </div>
          <AdvanceTable
            tableProps={{
              size: 'sm',
              className: 'phoenix-table fs-9 mb-0 border-top border-translucent'
            }}
            rowClassName="hover-actions-trigger btn-reveal-trigger position-static"
          />
          <AdvanceTableFooter
            navBtn
            showViewAllBtn={false}
            hasMore={hasNextPage}
            onNeedMore={handleNeedMore}
          />
          {loadingMore && (
            <p className="text-center text-600 fs-9 mb-0 mt-1">Loading more invoices…</p>
          )}
        </AdvanceTableProvider>
      </PhoenixDocCard.Body>
    </PhoenixDocCard>
  );
};

export default InvoicesManagementPanel;